// GitLabSettings.tsx
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/common-ui/card";
import { Input } from "../../../../ui/common-ui/input";
import { Label } from "../../../../ui/common-ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/common-ui/select";
import { Badge } from "../../../../ui/common-ui/badge";
import { SettingProps } from "../../interface";
import { useToast } from "../../../../ui/common-ui/use-toast";
import {
    fetchTenants,
    gitlabConfigure,
    gitlabDeleteConfiguration,
    gitlabGetConfiguration,
} from "../../Api/settingsApi";
import { ArrowLeft, CheckCircle, Plus, Trash2 } from "lucide-react";
import { FaGitlab } from "react-icons/fa6";
import { Button } from "../../../../ui/common-ui/button";

type TenantData = {
    tenantName: string;
    domainName?: string;
};

type GitlabConfiguration = {
    id?: number | string;
    tenantName: string;
    instanceUrl?: string;
    clientId: string;
    connected?: boolean;
    scopes?: string[];
    createdAt?: string;
    updatedAt?: string;
};

type ConfigureResponse = {
    authorizeUrl: string;
    tenantName: string;
    scopes: string[];
    instanceUrl: string;
};

type CtaMode = "configure" | "loading" | "connect";

const DEFAULT_INSTANCE_URL = "https://gitlab.com";
const DEFAULT_SCOPES = ["read_api", "read_user"];

export default function GitLabSettings({ onBack }: SettingProps) {
    const { toast } = useToast();

    const [tenants, setTenants] = useState<TenantData[]>([]);
    const [tenantName, setTenantName] = useState<string>("");

    const [configurations, setConfigurations] = useState<GitlabConfiguration[]>([]);
    const [selectedConfigId, setSelectedConfigId] = useState<string>("");
    const [loadingConfigurations, setLoadingConfigurations] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);

    const [formData, setFormData] = useState({
        clientId: "",
        clientSecret: "",
    });

    const [submitting, setSubmitting] = useState(false);
    const [ctaMode, setCtaMode] = useState<CtaMode>("configure");
    const [authorizeUrl, setAuthorizeUrl] = useState<string>("");
    const [pendingConnection, setPendingConnection] = useState(false);

    const selectedConfig = useMemo(
        () => configurations.find((item) => String(item.id ?? `${item.tenantName}-${item.instanceUrl}-${item.clientId}`) === selectedConfigId),
        [configurations, selectedConfigId]
    );

    const isFormValid =
        Boolean(tenantName) &&
        Boolean(formData.clientId.trim()) &&
        Boolean(formData.clientSecret.trim());

    const resetForm = () => {
        setFormData({
            clientId: "",
            clientSecret: "",
        });
        setAuthorizeUrl("");
        setCtaMode("configure");
        setPendingConnection(false);
    };

    const loadTenants = async () => {
        try {
            const response = await fetchTenants();
            const tenantList = Array.isArray(response) ? response : [];
            setTenants(tenantList);

            if (!tenantName && tenantList.length > 0) {
                const firstTenant = tenantList[0]?.tenantName || "";
                setTenantName(firstTenant);
            }
        } catch {
            toast({
                title: "Failed to load tenants",
                description: "Unable to fetch tenant list.",
                variant: "destructive",
            });
        }
    };

    const loadConfigurations = async (targetTenantName: string) => {
        if (!targetTenantName) return;

        setLoadingConfigurations(true);
        try {
            const response = await gitlabGetConfiguration(targetTenantName);

            const rawList = Array.isArray(response)
                ? response
                : Array.isArray(response?.configurations)
                    ? response.configurations
                    : response
                        ? [response]
                        : [];

            const list = rawList.map((item: any) => ({
                id: item.id ?? `${item.tenantName}-${item.instanceUrl}-${item.clientId}`,
                tenantName: item.tenantName || "",
                instanceUrl: item.instanceUrl || DEFAULT_INSTANCE_URL,
                clientId: item.clientId || "",
                connected: Boolean(item.connected),
                scopes: item.scopesCsv
                    ? String(item.scopesCsv).split(",").map((v: string) => v.trim()).filter(Boolean)
                    : Array.isArray(item.scopes)
                        ? item.scopes
                        : [],
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
            }));

            setConfigurations(list);

            if (list.length > 0) {
                const firstId = String(list[0].id);
                setSelectedConfigId(firstId);
                setShowAddForm(false);
            } else {
                setSelectedConfigId("");
                setShowAddForm(true);
            }
        } catch (e: any) {
            setConfigurations([]);
            setSelectedConfigId("");
            setShowAddForm(true);
            toast({
                title: "Failed to load configuration",
                description: e?.response?.data?.message || "Unable to fetch GitLab configuration.",
                variant: "destructive",
            });
        } finally {
            setLoadingConfigurations(false);
        }
    };

    useEffect(() => {
        loadTenants();
    }, []);

    useEffect(() => {
        if (tenantName) {
            loadConfigurations(tenantName);
        }
    }, [tenantName]);

    const handleConfigure = async () => {
        if (ctaMode === "connect") {
            if (!authorizeUrl) return;

            window.open(authorizeUrl, "_blank", "noopener,noreferrer");

            setSubmitting(true);
            try {
                await loadConfigurations(tenantName);
                setPendingConnection(false);
                setAuthorizeUrl("");
                setCtaMode("configure");

                toast({
                    title: "Connect flow started",
                    description: "Authorization URL opened. Configuration is now shown below.",
                });
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (!isFormValid) {
            toast({
                title: "Missing Information",
                description: "Please fill all required fields.",
                variant: "destructive",
            });
            return;
        }

        setSubmitting(true);
        setCtaMode("loading");

        try {
            const payload = {
                tenantName,
                instanceUrl: DEFAULT_INSTANCE_URL,
                clientId: formData.clientId.trim(),
                clientSecret: formData.clientSecret.trim(),
                scopes: DEFAULT_SCOPES,
                groupIds: [],
                projectIds: [],
                includeSubgroups: true,
            };

            const response: ConfigureResponse = await gitlabConfigure(payload);

            if (!response?.authorizeUrl) {
                throw new Error("Authorize URL was not returned");
            }

            setAuthorizeUrl(response.authorizeUrl);
            setPendingConnection(true);
            setCtaMode("connect");

            toast({
                title: "Configuration Saved",
                description: "Authorization URL created successfully. Click Connect to continue.",
            });
        } catch (e: any) {
            setCtaMode("configure");
            setPendingConnection(false);
            setAuthorizeUrl("");
            toast({
                title: "Configuration Failed",
                description: e?.response?.data?.message || e?.message || "Unable to configure GitLab OAuth App.",
                variant: "destructive",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const config = selectedConfig;
        if (!config || !tenantName) return;

        setDeleting(true);
        try {
            await gitlabDeleteConfiguration(
                tenantName,
                config.instanceUrl || DEFAULT_INSTANCE_URL
            );

            toast({
                title: "Configuration Deleted",
                description: "GitLab configuration and synced data removed successfully.",
            });

            await loadConfigurations(tenantName);
            resetForm();
        } catch (e: any) {
            toast({
                title: "Delete Failed",
                description:
                    e?.response?.data?.message || "Unable to delete GitLab configuration.",
                variant: "destructive",
            });
        } finally {
            setDeleting(false);
        }
    };

    const shouldShowConfiguredState = !!configurations.length && !showAddForm && !pendingConnection;
    const shouldShowForm = showAddForm || configurations.length === 0 || pendingConnection;

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="settings-back-button"
                            onClick={onBack}
                            aria-label="Back to settings"
                            title="Back to settings"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </Button>

                        <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl flex items-center justify-center border border-slate-200 dark:border-slate-700">
                            <FaGitlab className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>

                        <div className="flex-1">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                                        GitLab Setup
                                    </h1>
                                    <p className="text-muted-foreground">
                                        Configure GitLab OAuth integration
                                    </p>
                                </div>

                                {shouldShowConfiguredState && (
                                    <Button
                                        onClick={() => {
                                            resetForm();
                                            setShowAddForm(true);
                                        }}
                                        className="gap-2"
                                        data-testid="button-add-gitlab-configuration"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Another
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-border/40 rounded-2xl">
                <CardHeader>
                    <CardTitle>Tenant</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="max-w-md space-y-2">
                        <Label>Select Tenant</Label>
                        <Select value={tenantName} onValueChange={setTenantName}>
                            <SelectTrigger data-testid="select-tenant-name">
                                <SelectValue placeholder="Select tenant" />
                            </SelectTrigger>
                            <SelectContent>
                                {tenants.map((tenant) => (
                                    <SelectItem key={tenant.tenantName} value={tenant.tenantName}>
                                        {tenant.tenantName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {shouldShowConfiguredState && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950 p-3 rounded-2xl border border-green-200 dark:border-green-800 shadow-sm">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">GitLab App configured successfully</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {shouldShowConfiguredState && (
                    <div className="lg:col-span-2 space-y-4">
                        <div className="space-y-2">
                            {configurations.map((config) => {
                                const id = String(config.id ?? `${config.tenantName}-${config.instanceUrl}-${config.clientId}`);
                                const isSelected = selectedConfigId === id;

                                return (
                                    <Card
                                        key={id}
                                        className={`cursor-pointer transition-colors hover-elevate rounded-2xl ${isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-950" : "border-border/40"}`}
                                        onClick={() => setSelectedConfigId(id)}
                                        data-testid={`card-gitlab-config-${id}`}
                                    >
                                        <CardContent className="p-4 space-y-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-medium">{config.tenantName}</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {config.connected ? "Connected" : "Configured"}
                                                    </div>
                                                </div>
                                                <Badge variant="secondary">
                                                    {config.connected ? "Connected" : "Configured"}
                                                </Badge>
                                            </div>
                                            <div className="text-xs text-muted-foreground break-all">
                                                {config.instanceUrl || DEFAULT_INSTANCE_URL}
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className={shouldShowConfiguredState ? "lg:col-span-3" : "lg:col-span-5"}>
                    <Card className="border-border/40 rounded-2xl">
                        <CardHeader>
                            <CardTitle>
                                {shouldShowForm ? "Add GitLab Configuration" : "Configuration Details"}
                            </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-5">
                            {loadingConfigurations ? (
                                <div className="py-10 text-center text-muted-foreground">
                                    Loading configuration...
                                </div>
                            ) : shouldShowForm ? (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="gitlab-client-id">Client Id</Label>
                                            <Input
                                                id="gitlab-client-id"
                                                value={formData.clientId}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, clientId: e.target.value }))}
                                                placeholder="GitLab OAuth Application client ID"
                                                data-testid="input-gitlab-client-id"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="gitlab-client-secret">Client Secret</Label>
                                            <Input
                                                id="gitlab-client-secret"
                                                type="password"
                                                value={formData.clientSecret}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, clientSecret: e.target.value }))}
                                                placeholder="GitLab OAuth Application client secret"
                                                data-testid="input-gitlab-client-secret"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-2 pt-2">
                                        {configurations.length > 0 && !pendingConnection && (
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowAddForm(false);
                                                    resetForm();
                                                }}
                                                data-testid="button-cancel-gitlab-add"
                                            >
                                                Cancel
                                            </Button>
                                        )}

                                        <Button
                                            onClick={handleConfigure}
                                            disabled={(!isFormValid && ctaMode !== "connect") || submitting}
                                            className="gap-2 min-w-[140px]"
                                            data-testid="button-configure-gitlab"
                                        >
                                            {ctaMode === "loading" ? "Loading..." : ctaMode === "connect" ? "Connect" : "Configure"}
                                        </Button>
                                    </div>
                                </>
                            ) : selectedConfig ? (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Tenant</Label>
                                            <Input value={selectedConfig.tenantName} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Instance Url</Label>
                                            <Input value={selectedConfig.instanceUrl || DEFAULT_INSTANCE_URL} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Client Id</Label>
                                            <Input value={selectedConfig.clientId} readOnly />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Status</Label>
                                            <Input value={selectedConfig.connected ? "Connected" : "Configured"} readOnly />
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 pt-2">
                                        <Button
                                            variant="destructive"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="gap-2"
                                            data-testid="button-delete-gitlab-configuration"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            {deleting ? "Deleting..." : "Delete Configuration"}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="py-10 text-center text-muted-foreground">
                                    No configuration found.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
